import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { useEffect, useState } from "react";
import { ArrowLeft } from "react-bootstrap-icons";
import { Role, SubscriptionPlan, UserType } from "@/briefpoint-client";
import { Modal } from "@/components/Modals";
import { PlanCard, SignupHeader } from "@/components/Signup";
import SignupFooter from "@/components/Signup/SignupFooter";
import { useFirmApi } from "@/hooks/useApi";
import { useAuth } from "@/hooks/useAuth";
import useStripePlans from "@/hooks/useStripe";
import type { SubscriptionPlanType } from "@/lib/types";
import CardSetupForm from "@/screens/Signup/CardSetupForm";

const STRIPE_KEY: string = import.meta.env.VITE_STRIPE_KEY;

const stripePromise = loadStripe(STRIPE_KEY);

interface Props {
  isShow: boolean;
  onClose: (success: boolean) => void;
}

export default function SignUpModal({ isShow, onClose }: Props) {
  const plans = useStripePlans();
  const { user, firm } = useAuth();
  const firmApi = useFirmApi();
  const onConfirm = () => {};
  const [plan, setPlan] = useState(plans[SubscriptionPlan.Monthly]);
  const [monthly, setMonthly] = useState(false);
  const [isShowPayment, setIsShowPayment] = useState(false);
  const [userQuantity, setUserQuantity] = useState(1);
  const [paralegalQuantity, setParalegalQuantity] = useState(0);

  function handleClose(success = false) {
    onClose(success);
    setTimeout(() => setIsShowPayment(false), 500); // small delay to account for modal transition
  }

  function handleClick(plan: SubscriptionPlanType, getStarted: boolean) {
    if (getStarted) {
      window.open(
        "https://calendly.com/d/cqn6-9yx-wwq/briefpoint-demo",
        "_blank",
      );
    } else {
      setPlan(plan);
      setIsShowPayment(true);
    }
  }

  useEffect(() => {
    async function fetch() {
      if (firm) {
        const users = await firmApi.firmGetUsers({
          id: firm.id,
          includeInactive: false,
        });
        const userCount = users.filter(
          (x) => x.type !== UserType.Paralegal,
        ).length;
        setUserQuantity(userCount);
        setParalegalQuantity(users.length - userCount);
      }
    }
    fetch();
  }, [firmApi, firm]);

  const showAdminContent = !!user?.role?.includes(Role.FirmAdmin);

  function plansModalContent(showAdminContent: boolean) {
    return (
      <>
        <SignupHeader
          showAdminContent={showAdminContent}
          userIsInactive={!!user?.isInactive}
        />
        <div className="d-flex flex-column gap-3 px-5">
          {showAdminContent && (
            <>
              <div className="d-flex gap-4">
                {!monthly ? (
                  <PlanCard
                    plan={plans[SubscriptionPlan.Yearly]}
                    onClick={handleClick}
                    intervalSwitch={setMonthly}
                  />
                ) : (
                  <PlanCard
                    plan={plans[SubscriptionPlan.Monthly]}
                    onClick={handleClick}
                    intervalSwitch={setMonthly}
                  />
                )}
                <PlanCard
                  plan={plans[SubscriptionPlan.YearlyBundle]}
                  compareToPlan={plans[SubscriptionPlan.Monthly]}
                  onClick={handleClick}
                  intervalSwitch={setMonthly}
                />
              </div>
              {showAdminContent && <SignupFooter />}
            </>
          )}
        </div>
      </>
    );
  }

  function paymentModalContent() {
    return (
      <>
        {/* biome-ignore lint/a11y/useKeyWithClickEvents: TODO: replace with button */}
        <div
          className="d-inline-block mb-4 cursor-pointer p-0"
          onClick={() => setIsShowPayment(false)}
        >
          <ArrowLeft />{" "}
          <span className="text-decoration-underline">Back to all Plans</span>
        </div>
        <Elements stripe={stripePromise}>
          <CardSetupForm
            plan={plan}
            onFirmUpdated={() => handleClose(true)}
            attorneyQuantity={userQuantity}
            paralegalQuantity={paralegalQuantity}
          />
        </Elements>
      </>
    );
  }

  return (
    <Modal
      show={isShow}
      onClose={handleClose}
      onConfirm={onConfirm}
      size={isShowPayment ? "xl" : "lg"}
      title={isShowPayment ? `Sign up for the ${plan.title} Plan` : ""}
      showCancelButton={false}
      showConfirmButton={false}
      preventBackgroundClick
    >
      {isShowPayment
        ? paymentModalContent()
        : plansModalContent(showAdminContent)}
    </Modal>
  );
}
